type DraftStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
const DRAFT_TTL_MS = 10 * 60_000;
export interface RiskRepairTarget { title: string; category?: string }
export interface RiskRepairRequest { prompt: string; targets: RiskRepairTarget[] }

function draftKey(subscriberId: string, projectId: string): string {
  return `risk-repair:${encodeURIComponent(subscriberId)}:${encodeURIComponent(projectId)}`;
}

/** A single-use draft only. The subscriber must still choose to send it. */
export function queueRiskRepairDraft(
  subscriberId: string,
  projectId: string,
  prompt: string,
  storage?: DraftStorage,
  now = Date.now(),
  targets: RiskRepairTarget[] = [],
): boolean {
  if (!subscriberId || !projectId || !prompt.trim()) return false;
  try {
    (storage ?? sessionStorage).setItem(draftKey(subscriberId, projectId), JSON.stringify({ prompt, targets, expiresAt: now + DRAFT_TTL_MS }));
    return true;
  } catch { return false; }
}

export function takeRiskRepairRequest(
  subscriberId: string,
  projectId: string,
  storage?: DraftStorage,
  now = Date.now(),
): RiskRepairRequest | null {
  if (!subscriberId || !projectId) return null;
  try {
    const key = draftKey(subscriberId, projectId);
    const target = storage ?? sessionStorage;
    const raw = target.getItem(key);
    target.removeItem(key);
    if (!raw) return null;
    const value: unknown = JSON.parse(raw);
    if (!value || typeof value !== 'object' || !('prompt' in value) || !('expiresAt' in value)) return null;
    if (typeof value.prompt !== 'string' || typeof value.expiresAt !== 'number' || value.expiresAt <= now) return null;
    const targets = 'targets' in value && Array.isArray(value.targets) ? value.targets.filter((target: unknown): target is RiskRepairTarget =>
      Boolean(target && typeof target === 'object' && 'title' in target && typeof target.title === 'string'
        && (!('category' in target) || typeof target.category === 'string'))) : [];
    return { prompt: value.prompt, targets };
  } catch { return null; }
}

export function takeRiskRepairDraft(subscriberId: string, projectId: string, storage?: DraftStorage, now = Date.now()): string | null {
  return takeRiskRepairRequest(subscriberId, projectId, storage, now)?.prompt ?? null;
}

/** Recover the identity in a generated composer draft after a reload. An
 * edited or unmatched draft fails closed instead of inheriting checked boxes. */
export function riskRepairTargetsFromPrompt(text: string): RiskRepairTarget[] | null {
  if (!/^Fix this finding[^\n]*:\n\n|^Fix all \d+ open findings across my repos, highest severity first:\n\n/.test(text)) return null;
  if (!/Open (?:a pull request|pull requests) with the fix(?:es)? and tell me what changed\.$/.test(text.trim())) return null;
  const titles = [...text.matchAll(/^(?:\d+\. )?\*\*\[[A-Z]+\]\*\* ([^\n]+)$/gm)]
    .map((match: RegExpMatchArray) => match[1].split(' — ')[0].trim());
  return titles.length > 0 ? titles.map((title: string) => ({ title })) : null;
}

/** Exact normalized semantic identity. Multiple files with the same finding
 * require the subscriber to choose in the checklist, never a guessed fix. */
export function resolveRiskRepairTasks(targets: RiskRepairTarget[], tasks: Array<{ id: string; title: string; category?: string }>): { ids: string[]; error: string | null } {
  const normalized = (value: string): string => value.trim().replace(/\s+/g, ' ').toLowerCase();
  const ids: string[] = [];
  for (const target of targets) {
    const matches = tasks.filter((task) => normalized(task.title) === normalized(target.title)
      && (!target.category || (task.category && normalized(task.category) === normalized(target.category))));
    if (matches.length !== 1) return { ids: [], error: matches.length > 1
      ? `Several current issues match "${target.title}". Select the exact issue in the checklist and press Fix selected.`
      : `No current fixable issue matches "${target.title}". Refresh the findings or select its current issue in the checklist and press Fix selected.` };
    ids.push(matches[0].id);
  }
  return { ids: [...new Set(ids)], error: null };
}

/** API project ids can only target projects present in the subscriber's view. */
export function ownedRiskProjectIds(ids: string[], ownedIds: string[]): string[] {
  const owned = new Set(ownedIds);
  return [...new Set(ids)].filter((id: string) => owned.has(id));
}
